import { useMutation } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { BGVSelectedImage } from '@/components/gallery/BGVGallerySelector';

interface ImageUsageTrackingData {
  images: BGVSelectedImage[];
  socialPostId: string;
  platform: string;
  action: 'create' | 'update' | 'delete';
}

interface SocialMediaImageUsage {
  id?: string;
  image_id: number;
  image_type: string;
  gallery_id: number;
  social_post_id: string;
  platform: string;
  image_url: string;
  usage_date: string;
  created_at?: string;
}

// Track image usage in social media posts
const trackSocialMediaImageUsage = async (data: ImageUsageTrackingData) => {
  try {
    const { images, socialPostId, platform, action } = data;

    if (action === 'delete') {
      // Remove tracking records for this post
      const { error } = await supabase
        .from('social_media_image_usage')
        .delete()
        .eq('social_post_id', socialPostId);

      if (error) {
        console.error('Error removing image usage tracking:', error);
        throw error;
      }

      return { success: true, action: 'deleted' };
    }

    // For create/update actions, first remove existing records for this post
    if (action === 'update') {
      await supabase
        .from('social_media_image_usage')
        .delete()
        .eq('social_post_id', socialPostId);
    }

    // Create new tracking records
    const usageRecords: Omit<SocialMediaImageUsage, 'id' | 'created_at'>[] = images.map(image => ({
      image_id: image.bgv_image_id,
      image_type: image.bgv_image_type,
      gallery_id: image.gallery_id,
      social_post_id: socialPostId,
      platform: platform,
      image_url: image.url,
      usage_date: new Date().toISOString()
    }));

    // Insert usage tracking records
    const { data: insertedData, error: insertError } = await supabase
      .from('social_media_image_usage')
      .insert(usageRecords)
      .select();

    if (insertError) {
      console.error('Error inserting image usage tracking:', insertError);
      throw insertError;
    }

    // Update gallery used_for_social_media flag for all galleries involved
    const galleryIds = [...new Set(images.map(img => img.gallery_id))];
    
    for (const galleryId of galleryIds) {
      const { error: galleryError } = await supabase
        .from('galleries')
        .update({ used_for_social_media: true })
        .eq('id', galleryId);

      if (galleryError) {
        console.error(`Error updating gallery ${galleryId} social media flag:`, galleryError);
      }
    }

    console.log(`Successfully tracked ${images.length} images for social post ${socialPostId}`);
    
    return { 
      success: true, 
      action: action,
      recordsCreated: insertedData?.length || 0,
      galleriesUpdated: galleryIds.length
    };

  } catch (error) {
    console.error('Error in trackSocialMediaImageUsage:', error);
    throw error;
  }
};

// Get image usage statistics for a gallery
const getGalleryImageUsageStats = async (galleryId: number) => {
  try {
    const { data, error } = await supabase
      .from('social_media_image_usage')
      .select(`
        image_id,
        image_type,
        platform,
        usage_date,
        social_post_id
      `)
      .eq('gallery_id', galleryId)
      .order('usage_date', { ascending: false });

    if (error) {
      console.error('Error fetching gallery usage stats:', error);
      throw error;
    }

    // Calculate statistics
    const totalUsages = data?.length || 0;
    const uniqueImages = new Set(data?.map(item => `${item.image_type}-${item.image_id}`)).size;
    const platformStats = data?.reduce((acc, item) => {
      acc[item.platform] = (acc[item.platform] || 0) + 1;
      return acc;
    }, {} as Record<string, number>) || {};

    const lastUsed = data?.[0]?.usage_date ? new Date(data[0].usage_date) : null;

    return {
      galleryId,
      totalUsages,
      uniqueImages,
      platformStats,
      lastUsed,
      recentUsages: data?.slice(0, 10) || []
    };

  } catch (error) {
    console.error('Error in getGalleryImageUsageStats:', error);
    throw error;
  }
};

// Get image usage for a specific social post
const getSocialPostImageUsage = async (socialPostId: string) => {
  try {
    const { data, error } = await supabase
      .from('social_media_image_usage')
      .select(`
        *
      `)
      .eq('social_post_id', socialPostId)
      .order('usage_date', { ascending: false });

    if (error) {
      console.error('Error fetching social post image usage:', error);
      throw error;
    }

    return data || [];

  } catch (error) {
    console.error('Error in getSocialPostImageUsage:', error);
    throw error;
  }
};

// React hooks
export const useTrackImageUsage = () => {
  return useMutation({
    mutationFn: trackSocialMediaImageUsage,
    onSuccess: (data) => {
      console.log('Image usage tracking successful:', data);
    },
    onError: (error) => {
      console.error('Image usage tracking failed:', error);
    }
  });
};

export const useGalleryUsageStats = (galleryId: number) => {
  return useMutation({
    mutationFn: () => getGalleryImageUsageStats(galleryId),
    onError: (error) => {
      console.error('Failed to fetch gallery usage stats:', error);
    }
  });
};

export const useSocialPostImageUsage = (socialPostId: string) => {
  return useMutation({
    mutationFn: () => getSocialPostImageUsage(socialPostId),
    onError: (error) => {
      console.error('Failed to fetch social post image usage:', error);
    }
  });
};

// Export utility functions
export {
  trackSocialMediaImageUsage,
  getGalleryImageUsageStats,
  getSocialPostImageUsage
};

// Types for external use
export type { 
  ImageUsageTrackingData, 
  SocialMediaImageUsage 
};